import { motion, useInView } from 'framer-motion';
import { useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PaymentLogos } from '@/components/billing/PaymentLogos';
import { PromoBanner } from '@/components/billing/PromoBanner';
import { isPromoActive, PROMO_END_LABEL } from '@/lib/promo';

const appleEase = [0.22, 1, 0.36, 1] as const;
const duration = 0.8;
const stagger = 0.08;

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger },
  },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const slideUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Get started with the basics',
    subtitle: null,
    features: [
      '1 course',
      '150 credits/month',
      'Basic progress tracking',
    ],
    popular: false,
  },
  {
    name: 'Student',
    price: '$12.99',
    period: '/month',
    description: 'Everything you need to succeed',
    subtitle: null,
    features: [
      '14-day free trial',
      'Up to 6 courses',
      '1,200 credits/month',
      'Full study plan & reports',
      'Priority support',
    ],
    popular: false,
  },
  {
    name: 'Family',
    price: '$24.99',
    period: '/month',
    description: 'Support the whole family',
    subtitle: null,
    features: [
      '14-day free trial',
      'Everything in Student',
      '1,600 credits/month per student (more than Student)',
      'Unlimited student accounts',
      'Parent dashboard',
      'Dinner table conversations',
    ],
    popular: true,
  },
];

export default function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: '-100px' });
  const promo = isPromoActive();

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty('--mouse-x', `${x}%`);
    card.style.setProperty('--mouse-y', `${y}%`);

    // Subtle tilt toward the cursor — desktop pointers only, and never under reduced motion.
    const fine = window.matchMedia('(min-width: 768px) and (hover: hover)').matches;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || still) return;
    const tiltY = ((x - 50) / 50) * 4;
    const tiltX = ((50 - y) / 50) * 4;
    card.style.transition = 'transform 120ms ease-out';
    card.style.transform = `perspective(900px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-4px)`;
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    card.style.transition = 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)';
    card.style.transform = '';
  }, []);

  return (
    <section ref={ref} className="relative py-20 md:py-28 bg-background overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="text-center mb-12 md:mb-16"
        >
          <motion.span
            variants={fadeIn}
            transition={{ duration, ease: appleEase }}
            className="inline-block text-sm font-semibold tracking-wider uppercase text-accent mb-4"
          >
            Pricing
          </motion.span>

          <motion.h2
            variants={fadeIn}
            transition={{ duration, ease: appleEase, delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4"
          >
            {promo ? 'Free for everyone right now' : 'Simple, transparent pricing'}
          </motion.h2>

          <motion.p
            variants={fadeIn}
            transition={{ duration, ease: appleEase, delay: 0.2 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            {promo
              ? `Every feature is unlocked for every account until ${PROMO_END_LABEL}. No card, no plan, no catch. The prices below are what plans will cost from 1 December 2026 — nothing is charged before then.`
              : 'Start free — no credit card needed for the Free plan. Paid plans include a 14-day trial (card required). Credits reset monthly.'}
          </motion.p>
        </motion.div>

        {promo && (
          <div className="max-w-3xl mx-auto mb-12">
            <PromoBanner />
            <div className="mt-8 text-center">
              <Button asChild variant="accent" size="lg" className="font-semibold">
                <Link to="/role-select">Get Started — it's free</Link>
              </Button>
            </div>
          </div>
        )}

        {/* Plan Cards — pt-6 gives badge breathing room above the grid.
            Shown during the promo too, so parents can see the real post-promo prices. */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12 pt-6"
        >
          {plans.map((plan, index) => (
          <motion.div
              key={plan.name}
              variants={slideUp}
              transition={{ duration, ease: appleEase, delay: 0.3 + index * 0.1 }}
              /* Outer wrapper: relative + pt-5 gives badge room; overflow-visible so badge floats above */
              className={`relative pt-5 overflow-visible ${plan.popular ? 'scale-[1.02]' : ''}`}
            >
              {/* Most Popular badge — outside overflow-hidden so it never clips */}
              {plan.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 bg-accent text-accent-foreground text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg shadow-accent/30">
                    <Sparkles className="w-3 h-3" />
                    Best Value
                  </span>
                </div>
              )}

              {/* Glow border wrapper for popular card */}
              <div
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
                className={`relative rounded-2xl h-full ${
                  plan.popular
                    ? 'pricing-card-glow-border'
                    : 'pricing-card-shine border border-border'
                }`}
              >
                {/* Inner card surface — overflow-hidden clips shimmer to rounded corners */}
                <div className={`relative rounded-2xl p-6 md:p-8 flex flex-col h-full overflow-hidden ${
                  plan.popular
                    ? 'bg-gradient-to-br from-[hsl(var(--primary))] via-[hsl(213,60%,25%)] to-[hsl(213,55%,30%)]'
                    : 'bg-card/80 backdrop-blur-sm'
                }`}>

                  {/* Mouse-tracking spotlight */}
                  <div className={`card-spotlight ${plan.popular ? 'card-spotlight-gold' : ''}`} />

                  {/* Gold tint overlay for popular */}
                  {plan.popular && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-accent/[0.08] to-transparent pointer-events-none" />
                  )}

                  {/* Shimmer sweep overlay (non-popular cards) */}
                  {!plan.popular && (
                    <div className="pricing-shimmer-sweep absolute inset-0 pointer-events-none rounded-2xl" />
                  )}

                  <div className={`text-center mb-6 relative z-10 ${plan.popular ? 'pt-4' : ''}`}>
                    <h3 className={`text-xl font-semibold mb-2 ${plan.popular ? 'text-white' : 'text-foreground'}`}>{plan.name}</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-foreground'}`}>{plan.price}</span>
                      <span className={`text-sm ${plan.popular ? 'text-white/70' : 'text-muted-foreground'}`}>{plan.period}</span>
                    </div>
                    {plan.subtitle && (
                      <p className="text-accent text-xs font-semibold mt-1">{plan.subtitle}</p>
                    )}
                    <p className={`text-sm mt-2 ${plan.popular ? 'text-white/80' : 'text-muted-foreground'}`}>{plan.description}</p>
                  </div>

                  <ul className="space-y-3 mb-6 flex-grow relative z-10">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className={`w-5 h-5 shrink-0 mt-0.5 text-accent`} />
                        <span className={`text-sm ${plan.popular ? 'text-white/90' : 'text-foreground'}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    asChild
                    variant={plan.popular ? 'accent' : 'outline'}
                    className={`w-full relative z-10 ${plan.popular ? 'font-semibold' : ''}`}
                  >
                    <Link to="/role-select">Get Started</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust signals */}
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          transition={{ duration, ease: appleEase, delay: 0.6 }}
          className="space-y-4"
        >
          <p className="text-center text-xs text-muted-foreground max-w-xl mx-auto">
            {promo
              ? `Free for everyone until ${PROMO_END_LABEL} — no card needed. On 1 December 2026 your account moves to the Free plan unless you choose a paid one — nothing is charged automatically.`
              : 'Credits reset monthly. Complex questions use more credits. Fair use applies.'}
          </p>
          <PaymentLogos showPricingLink />
        </motion.div>
      </div>
    </section>
  );
}
